<script>
import {
  iAccordion,
  iAccordionItem,
  iAccordionItemSizes,
} from '@inscribe/signal';
import DocumentRightDataBox from '@/V2/documents/document-view/DocumentRightDataBox';
import { VERIFICATION_RESULT_SORT_ORDER } from '@/constants/analyst-constants';
import {
  ANALYST_GROUPS_CONFIG,
  ANALYST_VERIFICATIONS_CONFIG,
  RISKS_CONFIG,
} from '@/constants/analyst-config';
import InfoIcon from '@/V2/components/InfoIcon';
import SignalDetails from '@/V2/documents/document-view/signals/SignalDetails.vue';
import SignalMetadata from '@/V2/documents/document-view/signals/SignalMetadata.vue';
import { getVerificationTitle } from '@/V2/documents/document-view/document-review-agent/utils';
import DocumentReviewAgentDetails from '@/V2/documents/document-view/document-review-agent/DocumentReviewAgentDetails.vue';

export default {
  components: {
    DocumentRightDataBox,
    iAccordion,
    iAccordionItem,
    InfoIcon,
    SignalDetails,
    SignalMetadata,
    DocumentReviewAgentDetails,
  },
  props: {
    document: {
      type: Object,
      required: true,
    },
    currentFullGroup: {
      type: String,
    },
    currentVerificationComponent: {
      type: String,
    },
    currentVerification: {
      type: Object,
    },
    showLoadingState: {
      type: Boolean,
      default: false,
    },
    lastRun: {
      type: Object,
    },
  },
  data() {
    return { ANALYST_GROUPS_CONFIG, iAccordionItemSizes, showFullGroup: false };
  },
  watch: {
    currentFullGroup(newVal) {
      if (newVal === 'verifications') {
        this.openFullGroup(this.verificationsGroups[0]);
      } else {
        this.showFullGroup = false;
      }
    },
  },
  computed: {
    verificationsGroups() {
      const groups = [];
      const verifications = this.lastRun?.output?.verifications || [];
      verifications.forEach((verification) => {
        const verificationConfig =
          ANALYST_VERIFICATIONS_CONFIG[verification.type];
        if (verificationConfig) {
          const currentVerification = {
            ...verification,
            config: verificationConfig,
            title: getVerificationTitle(verification),
            subtitle: verification.title,
          };
          let existingGroup = groups.find(
            (group) => group.key === verificationConfig.group,
          );
          if (!existingGroup) {
            existingGroup = {
              key: verificationConfig.group,
              riskLevels: [],
            };
            groups.push(existingGroup);
          }
          let existingGroupRisk = existingGroup.riskLevels.find(
            (riskLevel) => riskLevel.key === verification.result,
          );
          if (!existingGroupRisk) {
            existingGroupRisk = {
              key: verification.result,
              verifications: [],
            };
            existingGroup.riskLevels.push(existingGroupRisk);
          }
          existingGroupRisk.verifications.push(currentVerification);
        }
      });

      // sort group risk Levels
      groups.forEach((group) => {
        group.riskLevels.sort((r1, r2) => {
          return VERIFICATION_RESULT_SORT_ORDER[r1.key] >
            VERIFICATION_RESULT_SORT_ORDER[r2.key]
            ? 1
            : -1;
        });

        // subtitle
        const riskLevel = group.riskLevels[0];
        group.subtitle = `${riskLevel.verifications.length}  ${RISKS_CONFIG[
          riskLevel.key
        ].label.toLowerCase()} ${
          riskLevel.verifications.length === 1
            ? 'verification'
            : 'verifications'
        } identified`;

        this.$emit('update:subtitle', group.subtitle);
      });

      return groups;
    },
  },
  methods: {
    openFullGroup(group) {
      this.showFullGroup = true;
      this.$nextTick(() => {
        this.$emit(
          'open-overlay',
          this.$refs[`documentReviewGroup${this.getKeyName(group.key)}`][0],
        );
      });
    },
    getKeyName(key) {
      return key?.charAt(0).toUpperCase() + key?.slice(1).toLowerCase();
    },
    openItemDetails(verification) {
      this.$emit('set-verification', verification);
      this.$nextTick(() => {
        this.$emit('open-overlay', this.$refs.documentReviewAgentDetails);
      });
    },
  },
};
</script>

<template>
  <!-- collapsed in column view -->
  <i-accordion-item
    v-for="group in verificationsGroups"
    :key="group.key"
    :mount-open="true"
    :title="ANALYST_GROUPS_CONFIG[group.key].label"
    :no-background="true"
    :size="iAccordionItemSizes.LARGE"
    class="half-margin"
  >
    <!-- title extra content -->
    <template #extra-title-content>
      <info-icon
        class="i-mr-4"
        :title="ANALYST_GROUPS_CONFIG[group.key].label"
        :description="ANALYST_GROUPS_CONFIG[group.key].tooltipInfo"
        :link-text="
          ANALYST_GROUPS_CONFIG[group.key].linkUrl ? 'Learn more' : null
        "
        :link-external-url="ANALYST_GROUPS_CONFIG[group.key].linkUrl"
      />
    </template>

    <!-- group content -->
    <document-right-data-box
      :is-loading="showLoadingState"
      :group="group"
      :compact-version="true"
      empty-state-title="No risk indicators identified"
      empty-state-subtitle="Document risk verifications will be displayed here"
      empty-state-icon="medium-file-block"
      @show-item-details="openItemDetails"
      @view-all="openFullGroup(group)"
    ></document-right-data-box>
  </i-accordion-item>

  <!-- dynamic groups details -->
  <div v-for="group in verificationsGroups" :key="group.key">
    <document-right-data-box
      v-if="showFullGroup"
      :ref="`documentReviewGroup${getKeyName(group.key)}`"
      :is-loading="showLoadingState"
      :group="group"
      :compact-version="false"
      class="verification-group"
      @show-item-details="openItemDetails"
    ></document-right-data-box>
  </div>

  <!-- details -->
  <document-review-agent-details
    v-if="
      currentVerification &&
      !['fraud', 'requirements', 'transaction'].includes(
        currentVerification?.detailsType,
      )
    "
    ref="documentReviewAgentDetails"
    :item="currentVerification"
    class="verification-details"
  >
  </document-review-agent-details>
</template>
